import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import {
  CalendarClock,
  Check,
  Filter,
  Pencil,
  Plus,
  RotateCw,
  Settings2,
  Trash2,
  X
} from 'lucide-react';
import { Page } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox, CheckboxField, Popover, PopoverContent, PopoverTrigger, Tooltip } from '@/components/ui/menu';
import { Combobox } from '@/components/ui/combobox';
import { Field, Input } from '@/components/ui/input';
import { DayInput, Select } from '@/components/ui/select';
import { ConfirmDialog, Dialog } from '@/components/ui/dialog';
import { EmptyState, ErrorState } from '@/components/ui/empty';
import { Pagination } from '@/components/ui/pagination';
import { SkeletonRow, SortableTh, TableScroll, TableShell, Td, Th, Tr } from '@/components/ui/table';
import { TimerWidget } from './TimerWidget';
import { PendingImportBar } from '@/features/imports/PendingImportBar';
import { ReviewImport, sourceLabel } from '@/features/imports/ReviewImport';
import { usePendingImport } from '@/hooks/useImports';
import {
  useCreateEntry,
  useDeleteEntry,
  useEntries,
  useToggleEntryInvoiced,
  useUpdateEntry
} from '@/hooks/useEntries';
import {
  useActivities,
  useClients,
  useCreateActivity,
  useCreateClient,
  useNameLookup,
  useTeamUsers
} from '@/hooks/useReference';
import { useBillingNouns, useSession } from '@/hooks/useSession';
import { useGeneralSettings } from '@/hooks/useSettings';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { hoursShort, formatDay, parseHours, today } from '@/lib/format';
import { clientOptions } from '@/lib/client-labels';
import type { EntryFilters, EntrySort, EntrySortField, ImportItem, PendingImport, TimeEntry } from '@/types';

// Skeleton bar widths per column (column width minus the cell padding), same
// order as the header: Date, Who, Client, Activity, Hours, Notes, Invoiced, Actions.
const COLUMN_WIDTHS = ['6rem', '5.5rem', '7.5rem', '6.5rem', '3.5rem', null, '3.5rem', '3.5rem'];

// Tentative rows from a pending import stop here; the rest is a link to the review sheet.
const PENDING_ROW_CAP = 50;

// The import sources offered on the empty timesheet (INT6a), in the hub's order.
const IMPORT_SOURCES: { id: string; label: string }[] = [
  { id: 'google_sheets', label: 'Google Sheets' },
  { id: 'excel', label: 'Excel' },
  { id: 'harvest', label: 'Harvest' },
  { id: 'toggl', label: 'Toggl' },
  { id: 'clockify', label: 'Clockify' },
  { id: 'paper', label: 'Paper' }
];

interface PendingRow {
  id: string;
  date: string;
  client: string;
  activity: string;
  hours: number;
  notes: string;
}

const str = (v: unknown) => (typeof v === 'string' ? v : '');
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);

/**
 * The pending entries of a staged import as table rows (ON15). Client names
 * come from the batch's groups; activities are not sent with the batch, so
 * the activity falls back to the key's text after `act:`.
 */
function pendingRowsOf(batch: PendingImport['batch'], filters: EntryFilters): PendingRow[] {
  if (!batch) return [];
  const groupNames = new Map(batch.groups.map((g) => [g.groupKey, g.name]));
  const activityItems = (batch.items as Partial<Record<string, ImportItem[]>>).activity ?? [];
  const activityNames = new Map<string, string>();
  for (const a of activityItems) {
    const key = str(a.payload.key) || str(a.payload.activityKey) || a.groupKey || '';
    const name = str(a.payload.name);
    if (key && name) activityNames.set(key, name);
  }
  const rows: PendingRow[] = [];
  for (const item of batch.items.entry) {
    if (item.status !== 'pending') continue;
    const date = str(item.payload.date);
    if (!date) continue;
    if (filters.dateFrom && date < filters.dateFrom) continue;
    if (filters.dateTo && date > filters.dateTo) continue;
    const clientKey = str(item.payload.clientKey);
    const activityKey = str(item.payload.activityKey);
    rows.push({
      id: item.id,
      date,
      client:
        (item.groupKey && groupNames.get(item.groupKey)) ||
        groupNames.get(clientKey) ||
        clientKey.replace(/^client:/, ''),
      activity: activityNames.get(activityKey) || activityKey.replace(/^act:/, ''),
      hours: num(item.payload.hours),
      notes: str(item.payload.notes)
    });
  }
  return rows;
}

export function TimesheetPage() {
  const session = useSession();
  const nouns = useBillingNouns();

  const [page, setPage] = useState(1);
  const [hideInvoiced, setHideInvoiced] = useLocalStorage('billable.hideInvoiced', false);
  const [scope, setScope] = useState<string>('me');
  const [filters, setFilters] = useState<EntryFilters>({});
  const [sort, setSort] = useState<EntrySort>({ field: null, order: 'desc' });

  const query = useMemo(
    () => ({ page, filters: { ...filters, hideInvoiced, scope }, sort }),
    [page, filters, hideInvoiced, scope, sort]
  );

  const entries = useEntries(query);
  const teamUsers = useTeamUsers();
  const { clientName, activityName } = useNameLookup();
  const generalSettings = useGeneralSettings();

  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TimeEntry | null>(null);

  const createEntry = useCreateEntry(session.user);
  const updateEntry = useUpdateEntry();
  const deleteEntry = useDeleteEntry();
  const toggleInvoiced = useToggleEntryInvoiced(hideInvoiced);

  const rows = entries.data?.items ?? [];
  const showEmployee = scope === 'all';

  // Entries from a staged import, shown tinted under the real rows (ON15).
  // They are not in `rows`, so the footer totals never count them.
  const pendingImport = usePendingImport();
  const pendingBatch = pendingImport.data?.batch ?? null;
  const pendingRows = useMemo(
    () => pendingRowsOf(pendingBatch, filters),
    [pendingBatch, filters]
  );
  const pendingShown = pendingRows.slice(0, PENDING_ROW_CAP);
  const pendingOverflow = pendingRows.length - pendingShown.length;

  // Member color per user id, only when the feature is on and the Who column
  // is showing. Black is the "unset" color and gets no tint.
  const memberColors = useMemo(() => {
    const map = new Map<string, string>();
    if (!showEmployee || !generalSettings.data?.useEmployeeColors) return map;
    for (const c of generalSettings.data.employeeColors) {
      if (c.color && c.color.toLowerCase() !== '#000000') map.set(c.userId, c.color);
    }
    return map;
  }, [showEmployee, generalSettings.data]);
  const activeFilterCount =
    (filters.clientId && filters.clientId !== 'all' ? 1 : 0) +
    (filters.activityId && filters.activityId !== 'all' ? 1 : 0) +
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    (filters.search ? 1 : 0);

  const setSortField = (field: EntrySortField | null, order: 'asc' | 'desc') => {
    setSort({ field, order });
    setPage(1);
  };

  const totalHours = rows.reduce((sum, r) => sum + r.time, 0);

  return (
    <Page
      wide
      title="Timesheet"
      description="Log the work you've done. Everything saves as you type."
      actions={
        <>
          <TimerWidget />
          <FiltersButton
            filters={filters}
            count={activeFilterCount}
            onApply={(next) => {
              setFilters(next);
              setPage(1);
            }}
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Table options">
                <Settings2 />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64">
              <CheckboxField
                checked={hideInvoiced}
                onCheckedChange={(v) => {
                  setHideInvoiced(v);
                  setPage(1);
                }}
                label="Hide invoiced entries"
                description="Keeps the list to work that still needs billing."
              />
            </PopoverContent>
          </Popover>
          <Tooltip content="Refresh">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Refresh"
              onClick={() => void entries.refetch()}
            >
              <RotateCw className={entries.isFetching ? 'animate-spin' : undefined} />
            </Button>
          </Tooltip>
        </>
      }
    >
      {/* An import waiting for its OK (ON15). The bar renders nothing otherwise,
          and `empty:hidden` keeps its wrapper from taking the margin. */}
      <div className="mb-4 empty:hidden">
        <PendingImportBar />
      </div>
      {/* The review sheet; opens itself from ?review=<batchId>. */}
      <ReviewImport />
      {session.isAdmin ? (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-muted">Showing</span>
          <Select
            aria-label="Whose entries to show"
            className="w-56"
            size="sm"
            value={scope}
            onChange={(v) => {
              setScope(v);
              setPage(1);
            }}
            options={[
              { value: 'me', label: 'My entries' },
              { value: 'all', label: 'Everyone on the team' },
              ...(teamUsers.data ?? [])
                .filter((u) => u.id !== session.user.id)
                .map((u) => ({ value: u.id, label: u.name ?? u.email ?? u.id }))
            ]}
          />
        </div>
      ) : null}

      <TableShell>
        {/* Every column is fixed-width except Notes, so the table fits its
            container; table-fixed keeps auto layout from widening cells to
            fit the add-row inputs. */}
        <TableScroll minWidth="min-w-0 table-fixed">
          <thead>
            <tr>
              <SortableTh field="date" active={sort.field} order={sort.order} onSort={setSortField} width="8.5rem">
                Date
              </SortableTh>
              {showEmployee ? (
                <SortableTh field="userName" active={sort.field} order={sort.order} onSort={setSortField} width="8rem">
                  Who
                </SortableTh>
              ) : null}
              <Th width="10rem">{nouns.client}</Th>
              <Th width="9rem">Activity</Th>
              <SortableTh field="time" active={sort.field} order={sort.order} onSort={setSortField} align="right" width="6rem">
                Hours
              </SortableTh>
              <Th>Notes</Th>
              <Th align="center" width="6rem" className="px-2">
                Invoiced
              </Th>
              <Th align="right" width="6rem" className="px-2">
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>

          <tbody>
            <AddEntryRow
              showEmployee={showEmployee}
              scope={scope}
              disabled={createEntry.isPending}
              onCreate={(input) => createEntry.mutate(input)}
            />

            {entries.isPending ? (
              Array.from({ length: 8 }).map((_, i) => (
                <SkeletonRow
                  key={i}
                  widths={
                    showEmployee
                      ? COLUMN_WIDTHS
                      : COLUMN_WIDTHS.filter((_, idx) => idx !== 1)
                  }
                />
              ))
            ) : entries.isError ? (
              <tr>
                <Td colSpan={showEmployee ? 8 : 7}>
                  <ErrorState
                    message={entries.error instanceof Error ? entries.error.message : undefined}
                    onRetry={() => void entries.refetch()}
                  />
                </Td>
              </tr>
            ) : rows.length === 0 && pendingShown.length === 0 ? (
              <tr>
                <Td colSpan={showEmployee ? 8 : 7}>
                  <EmptyState
                    icon={CalendarClock}
                    title="No time logged yet"
                    description={
                      activeFilterCount
                        ? 'No entries match these filters. Try clearing them.'
                        : 'Add your first entry using the row above, or start a timer.'
                    }
                    action={
                      activeFilterCount ? (
                        <Button variant="secondary" onClick={() => setFilters({})}>
                          <X />
                          Clear filters
                        </Button>
                      ) : (
                        <ImportPrompt />
                      )
                    }
                  />
                </Td>
              </tr>
            ) : (
              rows.map((entry) => {
                const color = memberColors.get(entry.userId);
                return (
                  <Tr
                    key={entry.id}
                    pending={entry.id.startsWith('tmp_')}
                    // The tint goes through a variable + utility class rather than
                    // an inline background so Tr's hover:bg-* class still wins.
                    className={color ? 'bg-[var(--row-tint)]' : undefined}
                    style={
                      color
                        ? ({ '--row-tint': `color-mix(in srgb, ${color} 18%, transparent)` } as CSSProperties)
                        : undefined
                    }
                  >
                    <Td className="whitespace-nowrap text-muted">{formatDay(entry.date)}</Td>
                    {showEmployee ? (
                      <Td className="truncate text-muted">
                        {color ? (
                          <span className="inline-flex max-w-full items-center gap-2">
                            <span
                              aria-hidden
                              className="size-2.5 shrink-0 rounded-full"
                              style={{ background: color }}
                            />
                            <span className="truncate">{entry.userName ?? '—'}</span>
                          </span>
                        ) : (
                          entry.userName ?? '—'
                        )}
                      </Td>
                    ) : null}
                    <Td className="truncate font-medium">{clientName(entry.clientId)}</Td>
                    <Td className="truncate">{activityName(entry.activityId)}</Td>
                    <Td align="right" className="tnum">
                      {hoursShort(entry.time)}
                    </Td>
                    <Td className="max-w-0 truncate text-muted">{entry.misc || '—'}</Td>
                    <Td align="center" className="px-2">
                      {entry.invoiced && entry.invoiceId ? (
                        <Badge tone="positive">
                          <Check className="size-3" />
                          Billed
                        </Badge>
                      ) : (
                        <Checkbox
                          aria-label="Mark as invoiced"
                          className="mx-auto"
                          checked={entry.invoiced}
                          onCheckedChange={(v) =>
                            toggleInvoiced.mutate({ id: entry.id, invoiced: v === true })
                          }
                        />
                      )}
                    </Td>
                    <Td align="right" className="px-2">
                      {entry.invoiced ? (
                        <span className="text-sm text-subtle">Locked</span>
                      ) : (
                        <div className="flex justify-end gap-0.5">
                          <Tooltip content="Edit">
                            <Button
                              variant="ghost"
                              size="iconSm"
                              aria-label="Edit entry"
                              onClick={() => setEditing(entry)}
                            >
                              <Pencil />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Delete">
                            <Button
                              variant="ghost"
                              size="iconSm"
                              aria-label="Delete entry"
                              className="hover:text-red-600 dark:hover:text-red-400"
                              onClick={() => setConfirmDelete(entry)}
                            >
                              <Trash2 />
                            </Button>
                          </Tooltip>
                        </div>
                      )}
                    </Td>
                  </Tr>
                );
              })
            )}

            {/* Tentative rows from the pending import, after the real ones.
                Never while the real rows are still loading or errored. */}
            {!entries.isPending && !entries.isError && pendingBatch
              ? pendingShown.map((row) => (
                  <Tr key={row.id} className="bg-[#fffbeb] dark:bg-amber-950/40">
                    <Td className="whitespace-nowrap text-muted">{formatDay(row.date)}</Td>
                    {showEmployee ? <Td className="truncate text-muted">—</Td> : null}
                    <Td className="truncate font-medium">{row.client || '—'}</Td>
                    <Td className="truncate">{row.activity || '—'}</Td>
                    <Td align="right" className="tnum">
                      {hoursShort(row.hours)}
                    </Td>
                    <Td className="max-w-0 truncate text-muted">{row.notes || '—'}</Td>
                    {/* Across Invoiced and the actions column, and short: the
                        old "From <source> · pending" badge in one 6rem cell
                        pushed the table past its container. The bar above
                        names the source. */}
                    <Td colSpan={2} align="right" className="px-2">
                      <span title={`From ${sourceLabel(pendingBatch.source)}, waiting for your OK`}>
                        <Badge tone="warning" className="text-xs">
                          Pending
                        </Badge>
                      </span>
                    </Td>
                  </Tr>
                ))
              : null}
            {!entries.isPending && !entries.isError && pendingBatch && pendingOverflow > 0 ? (
              <Tr className="bg-[#fffbeb] dark:bg-amber-950/40">
                <Td colSpan={showEmployee ? 8 : 7} className="text-sm text-muted">
                  and {pendingOverflow} more.{' '}
                  <Link
                    to={`?review=${pendingBatch.id}`}
                    className="font-medium text-[var(--accent)] underline-offset-4 hover:underline"
                  >
                    Review the import
                  </Link>
                </Td>
              </Tr>
            ) : null}
          </tbody>

          {rows.length > 0 || (pendingShown.length > 0 && !entries.isPending && !entries.isError) ? (
            <tfoot>
              <tr className="border-t border-[var(--line)] bg-[var(--surface-2)]">
                <Td colSpan={showEmployee ? 4 : 3} className="text-sm font-medium text-muted">
                  {rows.length} entries on this page
                  {pendingShown.length > 0 ? (
                    <span className="ml-3 font-normal text-subtle">
                      {pendingRows.length} pending {pendingRows.length === 1 ? 'entry' : 'entries'} not
                      counted
                    </span>
                  ) : null}
                </Td>
                <Td align="right" className="tnum font-semibold">
                  {hoursShort(totalHours)}
                </Td>
                <Td colSpan={3} />
              </tr>
            </tfoot>
          ) : null}
        </TableScroll>

        <Pagination
          label="entries"
          page={entries.data?.page ?? 1}
          totalPages={entries.data?.totalPages ?? 1}
          totalCount={entries.data?.totalCount ?? 0}
          pageSize={entries.data?.pageSize ?? 50}
          onChange={setPage}
        />
      </TableShell>

      <EditEntryDialog
        entry={editing}
        onClose={() => setEditing(null)}
        onSave={(patch) => {
          if (!editing) return;
          updateEntry.mutate({ id: editing.id, patch });
          setEditing(null);
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
        title="Delete this entry?"
        description={
          confirmDelete
            ? `${hoursShort(confirmDelete.time)}h on ${formatDay(confirmDelete.date)} for ${clientName(confirmDelete.clientId)}.`
            : ''
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (confirmDelete) deleteEntry.mutate({ id: confirmDelete.id });
          setConfirmDelete(null);
        }}
      />
    </Page>
  );
}

// ------------------------------------------------------- empty-state import

/** The import entry point on the empty timesheet (INT6a). */
function ImportPrompt() {
  const linkClass = 'font-medium text-[var(--accent)] underline-offset-4 hover:underline';
  return (
    <div className="flex flex-col items-center gap-3">
      <p className="text-sm text-muted">
        Already tracking time somewhere?{' '}
        <Link to="/manage/import" className={linkClass}>
          Import it
        </Link>
      </p>
      <div className="flex flex-wrap justify-center gap-1.5">
        {IMPORT_SOURCES.map((s) => (
          <Link
            key={s.id}
            to={`/manage/import?source=${s.id}`}
            className="rounded-full border border-[var(--line-strong)] px-2.5 py-0.5 text-xs font-medium text-muted transition-colors hover:bg-[var(--surface-hover)] hover:text-[var(--text)]"
          >
            {s.label}
          </Link>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------- add-entry row

function AddEntryRow({
  showEmployee,
  scope,
  disabled,
  onCreate
}: {
  showEmployee: boolean;
  scope: string;
  disabled?: boolean;
  onCreate: (input: {
    date: string;
    clientId: string;
    activityId: string;
    time: number;
    misc?: string;
    userId?: string;
  }) => void;
}) {
  const nouns = useBillingNouns();
  const clients = useClients();
  const activities = useActivities();
  const createClient = useCreateClient();
  const createActivity = useCreateActivity();

  const [date, setDate] = useState(today());
  const [clientId, setClientId] = useState<string | null>(null);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [hoursText, setHoursText] = useState('1');
  const [misc, setMisc] = useState('');

  const parsed = parseHours(hoursText);
  const valid = Boolean(clientId && activityId && parsed && parsed > 0);

  const submit = () => {
    if (!valid) return;
    onCreate({
      date,
      clientId: clientId!,
      activityId: activityId!,
      time: parsed!,
      misc: misc || undefined,
      userId: scope !== 'me' && scope !== 'all' ? scope : undefined
    });
    // The row clears immediately — the optimistic entry is already in the table.
    setActivityId(null);
    setHoursText('1');
    setMisc('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <tr data-tour="timesheet-entry-row" className="border-b border-[var(--line)] bg-[var(--surface-2)]">
      <Td className="px-2">
        <DayInput
          size="sm"
          aria-label="Date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          // Chrome's date field is wide by default (24px gap before the
          // calendar icon); trim it so the control fits the Date column.
          className="w-full min-w-0 px-2 [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:size-4 [&::-webkit-calendar-picker-indicator]:p-0"
        />
      </Td>
      {showEmployee ? <Td className="text-sm text-subtle">You</Td> : null}
      <Td>
        <Combobox
          size="sm"
          className="min-w-0"
          options={clientOptions(clients.data ?? [])}
          loading={clients.isPending}
          value={clientId}
          onChange={setClientId}
          placeholder={nouns.client}
          onCreate={async (name) => {
            const created = await createClient.mutateAsync({ name });
            setClientId(created.id);
          }}
          createLabel={`Add ${nouns.clientLower}`}
        />
      </Td>
      <Td>
        <Combobox
          size="sm"
          className="min-w-0"
          options={(activities.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
          loading={activities.isPending}
          value={activityId}
          onChange={setActivityId}
          placeholder="Activity"
          onCreate={async (name) => {
            const created = await createActivity.mutateAsync({ name });
            setActivityId(created.id);
          }}
          createLabel="Add activity"
        />
      </Td>
      <Td>
        <Input
          aria-label="Hours"
          className="h-8 w-full min-w-0 px-2 text-right tnum"
          value={hoursText}
          onChange={(e) => setHoursText(e.target.value)}
          onKeyDown={onKeyDown}
          invalid={hoursText.length > 0 && parsed === null}
          placeholder="1.5"
        />
      </Td>
      <Td>
        <Input
          aria-label="Notes"
          className="h-8 w-full min-w-0"
          value={misc}
          onChange={(e) => setMisc(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="What did you work on?"
        />
      </Td>
      <Td className="px-2" />
      <Td align="right" className="px-2">
        <Button size="sm" variant="primary" disabled={!valid || disabled} onClick={submit}>
          <Plus />
          Add
        </Button>
      </Td>
    </tr>
  );
}

// --------------------------------------------------------------- edit dialog

function EditEntryDialog({
  entry,
  onClose,
  onSave
}: {
  entry: TimeEntry | null;
  onClose: () => void;
  onSave: (patch: {
    date: string;
    clientId: string;
    activityId: string;
    time: number;
    misc: string | null;
  }) => void;
}) {
  const nouns = useBillingNouns();
  const clients = useClients();
  const activities = useActivities();
  const createClient = useCreateClient();
  const createActivity = useCreateActivity();

  const [date, setDate] = useState('');
  const [clientId, setClientId] = useState<string | null>(null);
  const [activityId, setActivityId] = useState<string | null>(null);
  const [hoursText, setHoursText] = useState('');
  const [misc, setMisc] = useState('');
  const [seeded, setSeeded] = useState<string | null>(null);

  // Seed from the entry the first time this particular entry opens.
  if (entry && seeded !== entry.id) {
    setSeeded(entry.id);
    setDate(entry.date);
    setClientId(entry.clientId);
    setActivityId(entry.activityId);
    setHoursText(hoursShort(entry.time));
    setMisc(entry.misc ?? '');
  }

  const parsed = parseHours(hoursText);
  const valid = Boolean(clientId && activityId && parsed && parsed > 0);

  return (
    <Dialog
      open={Boolean(entry)}
      onOpenChange={(v) => !v && onClose()}
      title="Edit entry"
      description="Changes appear in the table straight away."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!valid}
            onClick={() =>
              onSave({
                date,
                clientId: clientId!,
                activityId: activityId!,
                time: parsed!,
                misc: misc || null
              })
            }
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Date">
            <DayInput
              className="w-full"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field
            label="Hours"
            hint="Accepts 1.5, 1:30 or 90m."
            error={hoursText && parsed === null ? 'That is not a duration we recognize.' : null}
          >
            <Input
              className="tnum"
              value={hoursText}
              onChange={(e) => setHoursText(e.target.value)}
              invalid={Boolean(hoursText) && parsed === null}
            />
          </Field>
        </div>
        <Field label={nouns.client}>
          <Combobox
            options={clientOptions(clients.data ?? [])}
            value={clientId}
            onChange={setClientId}
            onCreate={async (name) => {
              const created = await createClient.mutateAsync({ name });
              setClientId(created.id);
            }}
            createLabel={`Add ${nouns.clientLower}`}
          />
        </Field>
        <Field label="Activity">
          <Combobox
            options={(activities.data ?? []).map((a) => ({ value: a.id, label: a.name }))}
            value={activityId}
            onChange={setActivityId}
            onCreate={async (name) => {
              const created = await createActivity.mutateAsync({ name });
              setActivityId(created.id);
            }}
            createLabel="Add activity"
          />
        </Field>
        <Field label="Notes">
          <Input value={misc} onChange={(e) => setMisc(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

// ------------------------------------------------------------------ filters

function FiltersButton({
  filters,
  count,
  onApply
}: {
  filters: EntryFilters;
  count: number;
  onApply: (filters: EntryFilters) => void;
}) {
  const nouns = useBillingNouns();
  const clients = useClients();
  const activities = useActivities();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EntryFilters>(filters);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) setDraft(filters);
      }}
    >
      <PopoverTrigger asChild>
        <Button variant={count ? 'secondary' : 'ghost'} size={count ? 'md' : 'icon'} aria-label="Filters">
          <Filter />
          {count ? <span className="tnum">{count}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        <Field label={nouns.client}>
          <Combobox
            options={[
              { value: 'all', label: `All ${nouns.clients.toLowerCase()}` },
              ...clientOptions(clients.data ?? [])
            ]}
            value={draft.clientId ?? 'all'}
            onChange={(v) => setDraft({ ...draft, clientId: v })}
          />
        </Field>
        <Field label="Activity">
          <Combobox
            options={[
              { value: 'all', label: 'All activities' },
              ...(activities.data ?? []).map((a) => ({ value: a.id, label: a.name }))
            ]}
            value={draft.activityId ?? 'all'}
            onChange={(v) => setDraft({ ...draft, activityId: v })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From">
            <DayInput
              className="w-full"
              value={draft.dateFrom ?? ''}
              onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
            />
          </Field>
          <Field label="To">
            <DayInput
              className="w-full"
              value={draft.dateTo ?? ''}
              onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
            />
          </Field>
        </div>
        <Field label="Search notes">
          <Input
            value={draft.search ?? ''}
            onChange={(e) => setDraft({ ...draft, search: e.target.value || undefined })}
            placeholder="e.g. deposition"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            onClick={() => {
              setDraft({});
              onApply({});
              setOpen(false);
            }}
          >
            Clear
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              onApply(draft);
              setOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
